// src/components/admin/account/AccountManagement.js
//
// Account Management — modern admin page for viewing, filtering, creating,
// editing, deleting users and changing roles. Rebuilt to match the HomeScreen
// style. Business logic is preserved: real-time users from Firestore, the
// `createUser` / `setUserPassword` callables, profile/role updates, and record
// deletion. New: changing a Student into a Tutor requires entering the tutor's
// teaching rate and admin rate before saving.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, functions } from "../../../firebaseConfig";
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { FiPlus, FiEdit2, FiTrash2, FiAlertTriangle, FiSearch } from "react-icons/fi";

import "./AccountManagement.css";

const ROLE_FILTERS = [
  { key: "all", label: "All" },
  { key: "student", label: "Students" },
  { key: "tutor", label: "Tutors" },
  { key: "admin", label: "Admins" },
];

// Roles are stored lowercase. Normalizing on read keeps a hand-edited value
// like "Tutor" from silently falling outside every role check in the app.
const KNOWN_ROLES = ["student", "tutor", "admin", "parent"];
function normalizeRole(value) {
  const role = String(value || "").trim().toLowerCase();
  return KNOWN_ROLES.includes(role) ? role : "";
}

function roleBadgeClass(role) {
  const r = String(role || "").toLowerCase();
  if (r === "admin") return "am-roleBadge--admin";
  if (r === "tutor") return "am-roleBadge--tutor";
  if (r === "student") return "am-roleBadge--student";
  return "am-roleBadge--other";
}

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const EMPTY_FORM = {
  name: "",
  email: "",
  role: "student",
  password: "",
  confirmPassword: "",
  newPassword: "",
  confirmNewPassword: "",
  teachingRate: "",
  adminRate: "",
};

export default function AccountManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formStatus, setFormStatus] = useState({ loading: false, error: "", success: "" });

  // Delete confirm modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snapshot) => {
      setUsers(snapshot.docs.map((d) => ({ uid: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const counts = useMemo(() => {
    const c = { all: users.length, student: 0, tutor: 0, admin: 0 };
    users.forEach((u) => {
      const r = String(u.role || "").toLowerCase();
      if (c[r] != null) c[r] += 1;
    });
    return c;
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users
      .filter((u) => filter === "all" || u.role === filter)
      .filter(
        (u) =>
          !term ||
          String(u.name || "").toLowerCase().includes(term) ||
          String(u.email || "").toLowerCase().includes(term)
      )
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [users, filter, search]);

  const setField = (patch) => setFormData((prev) => ({ ...prev, ...patch }));
  const resetStatus = () => setFormStatus({ loading: false, error: "", success: "" });

  const openCreateModal = () => {
    setEditMode(false);
    setSelectedUser(null);
    setFormData(EMPTY_FORM);
    resetStatus();
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditMode(true);
    setSelectedUser(user);
    // Rates are intentionally NOT pre-filled — existing tutor pay must not be
    // revealed here. They're only collected when promoting a student to a tutor.
    setFormData({
      ...EMPTY_FORM,
      name: user.name || "",
      email: user.email || "",
      role: normalizeRole(user.role) || "student",
    });
    resetStatus();
    setShowModal(true);
  };

  // --- Validation ---
  const validateCreatePasswords = () => {
    if (!formData.password || !formData.confirmPassword) return "Please enter and confirm a password.";
    if (formData.password.length < 6) return "Password must be at least 6 characters.";
    if (formData.password !== formData.confirmPassword) return "Passwords do not match.";
    return "";
  };
  const validateResetPasswordsIfProvided = () => {
    const a = formData.newPassword;
    const b = formData.confirmNewPassword;
    if (!a && !b) return "";
    if (!a || !b) return "To reset the password, fill in both new-password fields.";
    if (a.length < 6) return "New password must be at least 6 characters.";
    if (a !== b) return "New passwords do not match.";
    return "";
  };
  // Rates are only required where the fields are actually shown: promoting a
  // student → tutor, or creating a brand-new tutor.
  const isStudentToTutor = editMode && normalizeRole(selectedUser?.role) === "student" && formData.role === "tutor";
  const showRateFields = isStudentToTutor || (!editMode && formData.role === "tutor");

  const validateRates = () => {
    if (!showRateFields) return "";
    const t = Number(formData.teachingRate);
    const a = Number(formData.adminRate);
    if (formData.teachingRate === "" || formData.adminRate === "")
      return "Tutors require both a teaching rate and an admin rate.";
    if (!Number.isFinite(t) || t < 0 || !Number.isFinite(a) || a < 0)
      return "Pay rates must be valid non-negative numbers.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, error: "", success: "" });

    const fail = (msg) => setFormStatus({ loading: false, error: msg, success: "" });

    try {
      const rateErr = validateRates();
      if (rateErr) return fail(rateErr);

      if (editMode && selectedUser) {
        // --- EDIT ---
        const resetErr = validateResetPasswordsIfProvided();
        if (resetErr) return fail(resetErr);

        const previousRole = normalizeRole(selectedUser.role);
        const roleChanged = previousRole !== formData.role;

        const patch = { name: formData.name, role: formData.role };
        // Only write rates when promoting a student → tutor (the only place the
        // fields are shown). Existing tutors' rates are managed on the Pay Rate
        // page and must not be overwritten/blanked from here.
        if (isStudentToTutor) {
          patch.teachingRate = Number(formData.teachingRate);
          patch.adminRate = Number(formData.adminRate);
        }
        await updateDoc(doc(db, "users", selectedUser.uid), patch);

        // A role change takes effect for the user on their next page load: the
        // portal reads the role from this document, and the syncUserRoleClaim
        // backend trigger refreshes their auth claim right after this write.
        // Someone who is already signed in keeps seeing their old portal until
        // they refresh, so say so instead of closing the modal silently.
        const roleNote = roleChanged
          ? `${formData.name || "This user"} is now ${
              formData.role === "admin" ? "an" : "a"
            } ${formData.role}. They need to refresh the page (or sign out and back in) to see the ${
              formData.role
            } portal.`
          : "";

        if (formData.newPassword) {
          const setUserPasswordFn = httpsCallable(functions, "setUserPassword");
          await setUserPasswordFn({ uid: selectedUser.uid, newPassword: formData.newPassword });
          setFormStatus({
            loading: false,
            error: "",
            success: `Saved. Password updated.${roleNote ? ` ${roleNote}` : ""}`,
          });
          return;
        }
        if (roleNote) {
          setFormStatus({ loading: false, error: "", success: `Saved. ${roleNote}` });
          return;
        }
        setFormStatus({ loading: false, error: "", success: "" });
        setShowModal(false);
      } else {
        // --- CREATE ---
        const createErr = validateCreatePasswords();
        if (createErr) return fail(createErr);

        const createUserFn = httpsCallable(functions, "createUser");
        const res = await createUserFn({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: formData.role,
        });

        // Persist pay rates for newly-created tutors (best-effort: needs the uid
        // returned by the callable).
        if (formData.role === "tutor") {
          const newUid = res?.data?.uid || res?.data?.user?.uid || res?.data?.userId;
          if (newUid) {
            await updateDoc(doc(db, "users", newUid), {
              teachingRate: Number(formData.teachingRate),
              adminRate: Number(formData.adminRate),
            });
          }
        }
        setFormStatus({ loading: false, error: "", success: "" });
        setShowModal(false);
      }
    } catch (err) {
      console.error(err);
      fail(err.message || "Something went wrong.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "users", deleteTarget.uid));
      setDeleteTarget(null);
    } catch (err) {
      alert("Error deleting user: " + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Backdrop dismissal: only close when the press AND the release both land on
  // the backdrop itself. A plain onClick on the overlay also fires when a drag
  // *starts inside* the dialog and ends outside, because the click is dispatched
  // on the nearest common ancestor (the overlay) — that used to close the
  // Create/Edit modal mid text-selection.
  const backdropPressRef = useRef(false);

  const handleBackdropPointerDown = (e) => {
    backdropPressRef.current = e.target === e.currentTarget;
  };

  const handleBackdropPointerUp = (dismiss) => (e) => {
    const shouldDismiss = backdropPressRef.current && e.target === e.currentTarget;
    backdropPressRef.current = false;
    if (shouldDismiss) dismiss();
  };

  return (
    <div className="am-root">
      <div className="am-inner">
        {/* Header */}
        <div className="am-header">
          <div>
            <h1 className="am-title">Account Management</h1>
            <p className="am-subtitle">View, create, and manage user accounts, roles, and pay rates.</p>
          </div>
          <button className="am-primaryBtn" onClick={openCreateModal}>
            <FiPlus size={15} /> New User
          </button>
        </div>

        {/* Toolbar */}
        <div className="am-toolbar">
          <div className="am-pills">
            {ROLE_FILTERS.map((f) => (
              <button
                key={f.key}
                className={`am-pill ${filter === f.key ? "am-pill--active" : ""}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                <span className="am-pillCount">{counts[f.key] ?? 0}</span>
              </button>
            ))}
          </div>
          <div style={{ position: "relative", marginLeft: "auto", flex: "0 1 280px" }}>
            <FiSearch
              size={14}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
            />
            <input
              className="am-search"
              style={{ paddingLeft: 32, width: "100%" }}
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="am-card">
          <table className="am-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="am-empty">
                    Loading users…
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="am-empty">
                    No users match these filters.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.uid}>
                    <td>
                      <div className="am-userCell">
                        <div className="am-avatar">{initialsOf(user.name)}</div>
                        <div>
                          <div className="am-userName">{user.name || "Unnamed"}</div>
                          <div className="am-userEmail">{user.email || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`am-roleBadge ${roleBadgeClass(user.role)}`}>{user.role || "—"}</span>
                    </td>
                    <td>
                      <div className="am-rowActions">
                        <button className="am-iconAction" onClick={() => openEditModal(user)}>
                          <FiEdit2 size={13} /> Edit
                        </button>
                        <button className="am-iconAction am-iconAction--danger" onClick={() => setDeleteTarget(user)}>
                          <FiTrash2 size={13} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit modal */}
      {showModal && (
        <div
          className="am-overlay"
          onPointerDown={handleBackdropPointerDown}
          onPointerUp={handleBackdropPointerUp(() => setShowModal(false))}
        >
          <div className="am-modal" onClick={(e) => e.stopPropagation()}>
            <div className="am-modalHead">
              <h3 className="am-modalTitle">{editMode ? "Edit User" : "Create New User"}</h3>
              <button className="am-modalClose" onClick={() => setShowModal(false)} aria-label="Close">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="am-modalBody">
                {formStatus.error && <div className="am-msg am-msg--error">{formStatus.error}</div>}
                {formStatus.success && <div className="am-msg am-msg--success">{formStatus.success}</div>}

                <div className="am-field">
                  <label className="am-label">Full Name</label>
                  <input
                    className="am-input"
                    required
                    value={formData.name}
                    onChange={(e) => setField({ name: e.target.value })}
                  />
                </div>

                <div className="am-field">
                  <label className="am-label">Email</label>
                  <input
                    className="am-input"
                    type="email"
                    required
                    disabled={editMode}
                    value={formData.email}
                    onChange={(e) => setField({ email: e.target.value })}
                  />
                  {editMode && <div className="am-hint">Email can't be changed here.</div>}
                </div>

                {!editMode && (
                  <div className="am-row2">
                    <div className="am-field">
                      <label className="am-label">Password</label>
                      <input
                        className="am-input"
                        type="password"
                        required
                        minLength={6}
                        value={formData.password}
                        onChange={(e) => setField({ password: e.target.value })}
                      />
                    </div>
                    <div className="am-field">
                      <label className="am-label">Confirm Password</label>
                      <input
                        className="am-input"
                        type="password"
                        required
                        minLength={6}
                        value={formData.confirmPassword}
                        onChange={(e) => setField({ confirmPassword: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                <div className="am-field">
                  <label className="am-label">Role</label>
                  <select
                    className="am-select"
                    value={formData.role}
                    onChange={(e) => setField({ role: e.target.value })}
                  >
                    <option value="student">Student</option>
                    <option value="tutor">Tutor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Tutor pay rates — required for any tutor; highlighted when promoting a student. */}
                {showRateFields && (
                  <div className="am-section">
                    <div className="am-sectionTitle">Tutor Pay Rates</div>
                    {isStudentToTutor && (
                      <div className="am-notice">
                        <FiAlertTriangle size={15} style={{ flex: "0 0 auto", marginTop: 1 }} />
                        <span>
                          You're promoting a student to a tutor. Enter the teaching rate and admin rate before
                          saving — every tutor needs pay-rate information.
                        </span>
                      </div>
                    )}
                    <div className="am-row2" style={{ marginTop: isStudentToTutor ? 12 : 0 }}>
                      <div className="am-field" style={{ marginBottom: 0 }}>
                        <label className="am-label">Teaching Rate ($/hr)</label>
                        <input
                          className="am-input"
                          type="number"
                          min="0"
                          step="0.01"
                          required
                          value={formData.teachingRate}
                          onChange={(e) => setField({ teachingRate: e.target.value })}
                        />
                      </div>
                      <div className="am-field" style={{ marginBottom: 0 }}>
                        <label className="am-label">Admin Rate ($/hr)</label>
                        <input
                          className="am-input"
                          type="number"
                          min="0"
                          step="0.01"
                          required
                          value={formData.adminRate}
                          onChange={(e) => setField({ adminRate: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {editMode && (
                  <div className="am-section">
                    <div className="am-sectionTitle">Reset Password (optional)</div>
                    <div className="am-row2">
                      <div className="am-field" style={{ marginBottom: 0 }}>
                        <label className="am-label">New Password</label>
                        <input
                          className="am-input"
                          type="password"
                          minLength={6}
                          placeholder="Leave blank to keep"
                          value={formData.newPassword}
                          onChange={(e) => setField({ newPassword: e.target.value })}
                        />
                      </div>
                      <div className="am-field" style={{ marginBottom: 0 }}>
                        <label className="am-label">Confirm New Password</label>
                        <input
                          className="am-input"
                          type="password"
                          minLength={6}
                          placeholder="Re-type new password"
                          value={formData.confirmNewPassword}
                          onChange={(e) => setField({ confirmNewPassword: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="am-modalFoot">
                <button type="button" className="am-btn am-btn--ghost" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="am-btn am-btn--primary" disabled={formStatus.loading}>
                  {formStatus.loading ? "Saving…" : editMode ? "Save Changes" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div
          className="am-overlay"
          onPointerDown={handleBackdropPointerDown}
          onPointerUp={handleBackdropPointerUp(() => {
            if (!deleting) setDeleteTarget(null);
          })}
        >
          <div className="am-modal am-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="am-modalBody">
              <div className="am-confirmIcon">
                <FiTrash2 size={19} />
              </div>
              <h3 className="am-modalTitle" style={{ marginBottom: 8 }}>
                Delete user?
              </h3>
              <div className="am-confirmText">
                This removes the account record for{" "}
                <span className="am-confirmName">{deleteTarget.name || deleteTarget.email}</span>. This can't be
                undone. (Full auth deletion may require the Admin SDK.)
              </div>
            </div>
            <div className="am-modalFoot">
              <button className="am-btn am-btn--ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="am-btn am-btn--danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
